import React, { useState, useCallback } from 'react';
import { RenderFormatteContent, slice } from '../../utils';
import { iInterviewContent, typeProcess } from '../../components/type';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../stores';
import { FamlaServices } from '../../services';
import { formatInterviewScrollHeader, getChatTimestampHoverTitle } from '../../utils/formatDate';

interface iprops {
    fullname?: string | null;
    questionData: iInterviewContent;
    file: File | null;
    fileIn: boolean | undefined;
    index: number;
    onFileSelect: (file: File) => void;
    updateContent: (answer_id: string, content: string,) => void;
    lastAnsweredIndex: number
    processType: typeProcess
    isOwner?: boolean;
    hasStakeholders?: boolean;
    processTitle?: string;
    isCompleted?: boolean;
    /** False = lecture seule (contribution d'un autre) : pas d'édition / suppression */
    canWrite?: boolean;
    onDeleteSuccess?: () => void;
    ownerAsStakeholder?: boolean;
    useModernLayout?: boolean;
}

const ContentQuestion = (props: iprops) => {
    const {
        fullname, questionData, file, fileIn, index, updateContent,
        onFileSelect, lastAnsweredIndex, processType,
        isOwner, hasStakeholders, processTitle, canWrite = true, onDeleteSuccess, ownerAsStakeholder,
        useModernLayout = false
    } = props;

    const displayFullname = typeof fullname === 'string' ? fullname.trim() : '';
    const avatarInitials = (displayFullname.slice(0, 2) || '?').toUpperCase();

    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [modifiedResponse, setModifiedResponse] = useState(questionData.content || '');
    const [fileName, setFileName] = useState('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showComplexDeleteModal, setShowComplexDeleteModal] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const role = useAppSelector((state) => state.role.role);
    const users = useAppSelector((state) => state.auth);

    const handleSave = (answer_id: string, content: string) => {
        if (modifiedResponse.trim().length > 0) {
            updateContent(answer_id, content);
            setIsEditing(false);
            setModifiedResponse('')
        }
    };

    const handleEdit = () => {
        setModifiedResponse(questionData.content || '');
        setIsEditing(true);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setDeleteError('');
        try {
            const data = await FamlaServices("api_host", `/answer/delete-from-here/${questionData._id}`, "DELETE", "", users.access_token);
            if (data.status === 200) {
                setShowDeleteModal(false);
                setShowComplexDeleteModal(false);
                if (onDeleteSuccess) {
                    onDeleteSuccess();
                }
            } else {
                setDeleteError(data.body?.description || 'An error occurred during deletion');
            }
        } catch (error) {
            setDeleteError('Failed to connect to the server');
        } finally {
            setIsDeleting(false);
        }
    };

    const confirmComplexDelete = () => {
        const expected = t("popupMessage.deleteInterviewsOf") + processTitle;
        if (deleteConfirmationText !== expected) {
            setDeleteError(t("popupMessage.textDontMatch"));
            return;
        }
        handleDelete();
    };

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (isValidFile(file)) {
                onFileSelect(file);
                setFileName(file.name);
            }
        },
        [onFileSelect]
    );

    const handleFileInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file && isValidFile(file)) {
                onFileSelect(file);
                setFileName(file.name);
            }
        },
        [onFileSelect]
    );

    const isValidFile = (file: File): boolean => {
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const maxSize = 5 * 1024 * 1024;
        return validTypes.includes(file.type) && file.size <= maxSize;
    };

    const showEditButton = Boolean(canWrite) && !isEditing && Boolean(questionData.content) && lastAnsweredIndex === index;
    const showDeleteButton = Boolean(canWrite) && !isEditing && Boolean(questionData.content);
    const formattedTime = questionData.updated_at || questionData.created_at;
    const timeLabel =
        useModernLayout && formattedTime ? formatInterviewScrollHeader(formattedTime) : '';
    const timeHoverTitle =
        useModernLayout && formattedTime ? getChatTimestampHoverTitle(formattedTime, new Date()) : undefined;

    const renderContent = () => {
        if (isEditing) {
            return (
                <div className="cq-edit-panel">
                    <textarea
                        value={modifiedResponse}
                        onChange={(e) => setModifiedResponse(e.target.value)}
                        className="form-control cq-edit-textarea"
                        rows={5}
                    />
                    <div className="mt-2 d-flex justify-content-between align-items-center">
                        <div className="cq-edit-meta">
                            {fileName && (
                                <span className='bi bi-file-earmark-arrow-up fs-5 teal'> <span className='fs-6'>{slice(fileName, 5) + '.PDF'}</span></span>
                            )}
                        </div>

                        <div className="d-flex">
                            {(fileIn || file) && index === 1 &&
                                <div>
                                    <label
                                        htmlFor={`file-upload-${index}`}
                                        className="btn btn-outline-secondary d-flex justify-content-center align-items-center cq-edit-upload border-0 rounded-circle me-1"
                                        style={{ width: '40px', height: '40px', cursor: 'pointer' }}
                                    >
                                        <i className="bi bi-upload"></i>
                                    </label>
                                    <input
                                        id={`file-upload-${index}`}
                                        type="file"
                                        onChange={handleFileInput}
                                        style={{ display: 'none' }}
                                    />
                                </div>}

                            <button
                                onClick={() => setIsEditing(false)}
                                className="btn btn-sm rounded-pill border-0 cq-edit-cancel"
                            >
                                {t('words.cancel')}
                            </button>

                            <button
                                onClick={() => handleSave(questionData._id, modifiedResponse)}
                                className="btn btn-sm ms-1 rounded-pill border-0 cq-edit-send"
                            >
                                {t('words.send')}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                className={useModernLayout
                    ? 'cq-bubble-text'
                    : 'fs-6 fw-lighter custom-control m-0 text-light'
                }
            >
                {questionData.content && RenderFormatteContent(questionData.content)}
                {(
                    (
                        ((fileIn || file) && index === 1 && role !== "WORKER") ||
                        (processType === "HUMAN" && fileIn) || questionData.content?.trim().length === 0
                    ) && (
                        <i className="bi bi-file-earmark-arrow-up fs-1 text-secondary"></i>
                    )
                )}

            </div>
        );
    };

    if (useModernLayout) {
        return (
            <div className="turn human human-turn">
                <div className="t-avatar t-human">{avatarInitials}</div>
                <div className="t-body">
                    <div className="t-sender s-human">
                        {displayFullname || '—'}
                        {timeLabel && (
                            <span className="t-time" title={timeHoverTitle}>
                                {timeLabel}
                            </span>
                        )}
                    </div>
                    <div className="bubble-wrap">
                        {(showEditButton || showDeleteButton) && (
                            <div className="human-actions">
                                {showEditButton && (
                                    <button className="msg-btn" title={t('words.edit')} onClick={handleEdit}>
                                        <svg viewBox="0 0 16 16" fill="none">
                                            <path d="M11 2L14 5L5 14H2V11L11 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                )}
                                {showDeleteButton && (
                                    <button className="msg-btn danger" title={t('words.delete')} onClick={() => setShowDeleteModal(true)}>
                                        <svg viewBox="0 0 16 16" fill="none">
                                            <path d="M3 5H13M6 5V3H10V5M5 5L5.5 13H10.5L11 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        )}
                        <div className={`bubble${isEditing ? ' bubble-editing' : ''}`}>
                            {renderContent()}
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modals */}
                {showDeleteModal && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
                        <div className="bg-dark p-4 rounded-4 border border-secondary" style={{ maxWidth: '400px' }}>
                            <h4 className="text-danger mb-3">{t('words.danger')}</h4>
                            <p className="text-light">
                                {isOwner && hasStakeholders && !ownerAsStakeholder
                                    ? t('popupMessage.deleteOwnerMessages')
                                    : t('popupMessage.deleteStakeholderMessages')
                                }
                            </p>
                            {deleteError && <div className="text-danger small mb-3">{deleteError}</div>}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button className="btn btn-secondary rounded-pill" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                                    {t('words.cancel')}
                                </button>
                                <button
                                    className="btn btn-danger rounded-pill"
                                    onClick={() => {
                                        if (isOwner && hasStakeholders && !ownerAsStakeholder) {
                                            setShowDeleteModal(false);
                                            setShowComplexDeleteModal(true);
                                        } else {
                                            handleDelete();
                                        }
                                    }}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                                    {t('words.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showComplexDeleteModal && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
                        <div className="bg-dark p-4 rounded-4 border border-secondary" style={{ maxWidth: '500px' }}>
                            <h4 className="text-danger mb-3">{t("popupMessage.finalConfirmation")}</h4>
                            <p className="text-light mb-3">
                                {t("popupMessage.deleteProcess")}
                            </p>
                            <div className="bg-black p-2 rounded mb-3 text-info text-center fw-bold">
                                {t("popupMessage.deleteInterviewsOf") + processTitle}
                            </div>
                            <input
                                type="text"
                                className="form-control bg-dark text-light border-secondary mb-2"
                                placeholder={t("popupMessage.typeThePhraseHere")}
                                value={deleteConfirmationText}
                                onChange={(e) => {
                                    setDeleteConfirmationText(e.target.value);
                                    setDeleteError('');
                                }}
                            />
                            {deleteError && <div className="text-danger small mb-3">{deleteError}</div>}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button className="btn btn-secondary rounded-pill" onClick={() => { setShowComplexDeleteModal(false); setDeleteConfirmationText(''); setDeleteError(''); }} disabled={isDeleting}>
                                    {t('words.cancel')}
                                </button>
                                <button className="btn btn-danger rounded-pill" onClick={confirmComplexDelete} disabled={isDeleting}>
                                    {isDeleting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                                    {t("popupMessage.deleteAll")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="mt-4 d-flex align-items-center content-container">
            {(!isEditing && Boolean(canWrite) && questionData.content) ? (
                <div className="d-flex flex-column gap-2 me-3">
                    {lastAnsweredIndex === index && (
                        <button
                            onClick={handleEdit}
                            className="btn btn-outline-secondary d-flex justify-content-center align-items-center text-light border-0 rounded-circle"
                            style={{ width: '40px', height: '40px' }}
                            title={t('words.edit')}
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                    )}
                    <button
                        onClick={() => setShowDeleteModal(true)}
                        className="btn btn-outline-danger d-flex justify-content-center align-items-center text-light border-0 rounded-circle"
                        style={{ width: '40px', height: '40px' }}
                        title={t('words.delete')}
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            ) :
                (
                    <div
                        className="d-flex justify-content-center align-items-center text-light border-0 rounded-circle edit-btn me-3"
                        style={{ width: '40px', height: '40px' }}
                    >
                    </div>
                )}

            <div className="w-100 bg-dark p-3 rounded-4 card me-3">
                <div className="d-flex align-items-center">
                    <i className="bi bi-person fs-5 text-light"></i>
                    <span className="ps-1 text-light">{displayFullname || '—'}</span>
                </div>

                <div className="mt-2">
                    {renderContent()}
                </div>
            </div>

            {/* Delete Confirmation Modals */}
            {showDeleteModal && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
                    <div className="bg-dark p-4 rounded-4 border border-secondary" style={{ maxWidth: '400px' }}>
                        <h4 className="text-danger mb-3">{t('words.danger')}</h4>
                        <p className="text-light">
                            {isOwner && hasStakeholders && !ownerAsStakeholder //En tant que stakeholder, le owner n'a pas de sous interview qu'il peut supprimer. donc il a le même texte que les stakeholders classiques
                                ? t('popupMessage.deleteOwnerMessages')
                                : t('popupMessage.deleteStakeholderMessages')
                            }
                        </p>
                        {deleteError && <div className="text-danger small mb-3">{deleteError}</div>}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-secondary rounded-pill" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                                {t('words.cancel')}
                            </button>
                            <button
                                className="btn btn-danger rounded-pill"
                                onClick={() => {
                                    if (isOwner && hasStakeholders && !ownerAsStakeholder) {
                                        setShowDeleteModal(false);
                                        setShowComplexDeleteModal(true);
                                    } else {
                                        handleDelete();
                                    }
                                }}
                                disabled={isDeleting}
                            >
                                {isDeleting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                                {t('words.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showComplexDeleteModal && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
                    <div className="bg-dark p-4 rounded-4 border border-secondary" style={{ maxWidth: '500px' }}>
                        <h4 className="text-danger mb-3">{t("popupMessage.finalConfirmation")}</h4>
                        <p className="text-light mb-3">
                            {t("popupMessage.deleteProcess")}
                        </p>
                        <div className="bg-black p-2 rounded mb-3 text-info text-center fw-bold">
                            {t("popupMessage.deleteInterviewsOf") + processTitle}
                        </div>
                        <input
                            type="text"
                            className="form-control bg-dark text-light border-secondary mb-2"
                            placeholder={t("popupMessage.typeThePhraseHere")}
                            value={deleteConfirmationText}
                            onChange={(e) => {
                                setDeleteConfirmationText(e.target.value);
                                setDeleteError('');
                            }}
                        />
                        {deleteError && <div className="text-danger small mb-3">{deleteError}</div>}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-secondary rounded-pill" onClick={() => { setShowComplexDeleteModal(false); setDeleteConfirmationText(''); setDeleteError(''); }} disabled={isDeleting}>
                                {t('words.cancel')}
                            </button>
                            <button className="btn btn-danger rounded-pill" onClick={confirmComplexDelete} disabled={isDeleting}>
                                {isDeleting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                                {t("popupMessage.deleteAll")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentQuestion;
